import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Loader2, Instagram, Youtube, Mail, Share2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

// Lucide doesn't ship TikTok/Threads/X/Discord icons that match the brands —
// use inline SVG so each button is unmistakable to the eye.
const Icon = ({ k, size = 22 }) => {
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "currentColor" };
  switch (k) {
    case "tiktok":    return <svg {...common}><path d="M19.5 7.5a7.5 7.5 0 0 1-4.5-1.5v8.25a5.25 5.25 0 1 1-5.25-5.25c.3 0 .6.03.9.09V12a2.4 2.4 0 1 0 1.65 2.28V2.25h2.7a4.8 4.8 0 0 0 4.5 4.5v.75z"/></svg>;
    case "threads":   return <svg {...common}><path d="M18 11.3c-.3-1.5-1.2-2.6-2.4-3.1 0-1.4-1.1-2.4-2.6-2.4-1.1 0-2.1.5-2.6 1.5l1.3.7c.3-.5.8-.8 1.3-.8.7 0 1.3.4 1.3 1.1-1.6-.3-3.2.3-4 1.6-.7 1.2-.3 2.7.9 3.5 1.4.9 3.3.2 4.1-1.5.3-.6.4-1.3.4-1.9.5.3.8.7 1 1.2.3.9.1 1.9-.6 2.7-.8 1-2.2 1.5-3.9 1.5-2.6 0-4.5-1.8-4.5-5.1s1.8-5.1 4.5-5.1c1.9 0 3.3.9 4 2.2l1.3-.7C14.7 5.1 12.7 4 10.5 4 7.1 4 4.5 6.6 4.5 11s2.6 7 6 7c2 0 3.7-.6 4.9-1.9 1-1.1 1.3-2.5.9-3.7-.1-.4-.2-.7-.3-1.1z"/></svg>;
    case "x":         return <svg {...common}><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>;
    case "discord":   return <svg {...common}><path d="M20.3 4.4a19 19 0 0 0-4.7-1.5l-.2.4a17 17 0 0 1 4.2 1.3 15 15 0 0 0-15.2 0A17 17 0 0 1 8.6 3.3l-.2-.4A19 19 0 0 0 3.7 4.4C.8 8.8 0 13 .4 17.2a19 19 0 0 0 5.8 2.9l1.1-1.7c-1-.4-1.9-.9-2.7-1.6.2-.2.5-.3.7-.5a13 13 0 0 0 11.4 0c.2.2.5.3.7.5-.8.7-1.7 1.2-2.7 1.6l1.1 1.7a19 19 0 0 0 5.8-2.9c.4-5-1-9-3.3-12.8zM8.5 14.6c-1.1 0-2-1-2-2.3s.9-2.3 2-2.3 2 1 2 2.3-.9 2.3-2 2.3zm7 0c-1.1 0-2-1-2-2.3s.9-2.3 2-2.3 2 1 2 2.3-.9 2.3-2 2.3z"/></svg>;
    case "facebook":  return <svg {...common}><path d="M22 12A10 10 0 1 0 10.4 21.9V14.9H7.9V12h2.5V9.9c0-2.5 1.5-3.9 3.8-3.9 1.1 0 2.2.2 2.2.2v2.5h-1.3c-1.2 0-1.6.8-1.6 1.6V12h2.8l-.4 2.9h-2.3v6.9A10 10 0 0 0 22 12z"/></svg>;
    default: return null;
  }
};

const SOCIAL_META = {
  tiktok:     { label: "TikTok",    icon: "tiktok" },
  instagram:  { label: "Instagram", icon: "ig" },
  youtube:    { label: "YouTube",   icon: "yt" },
  discord:    { label: "Discord",   icon: "discord" },
  threads:    { label: "Threads",   icon: "threads" },
  x:          { label: "X",         icon: "x" },
  facebook:   { label: "Facebook",  icon: "facebook" },
};

const Follow = () => {
  const [state, setState] = useState({ loading: true, links: {}, recent: [] });
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/follow").then((r) => setState({ loading: false, ...r.data }))
      .catch(() => setState({ loading: false, links: {}, recent: [] }));
    api.post("/events", { event: "follow_page_viewed" }).catch(() => {});
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/leads", { email, source: "follow_page" });
      toast.success("You're on the list.");
      setEmail("");
      api.post("/events", { event: "follow_page_signup" }).catch(() => {});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't sign you up. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const sharePage = async () => {
    const text = "Follow wasntme.party for mystery nights, puzzles, and horror cases.";
    const url = `${window.location.origin}/follow`;
    if (navigator.share) {
      try { await navigator.share({ title: "wasntme.party", text, url }); } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(`${text} ${url}`);
      toast.success("Share link copied.");
    }
  };

  if (state.loading) return <div className="grain min-h-screen flex items-center justify-center text-[#a39797]"><Loader2 className="animate-spin" /></div>;

  const links = state.links || {};
  const entries = Object.entries(SOCIAL_META).filter(([k]) => links[k]);

  const customIcon = (k) => {
    if (k === "instagram") return <Instagram size={22} />;
    if (k === "youtube")   return <Youtube size={22} />;
    return <Icon k={SOCIAL_META[k].icon} />;
  };

  return (
    <div className="grain min-h-screen" data-testid="follow-page">
      <div className="max-w-xl mx-auto px-6 py-12 text-center">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#a39797] hover:text-[#d4af37] mb-8">
          <ArrowLeft size={16} /> back to the file
        </Link>
        <div className="font-hand text-[#d4af37] rotate-[-1deg] text-2xl mb-2">follow the case</div>
        <h1 className="font-display text-4xl sm:text-5xl mb-4">Everywhere we post.</h1>
        <p className="text-[#a39797] mb-8">
          Mystery drops, horror case announcements, Global Mystery Night, and the Daily Whodunit — all in one place.
        </p>

        {/* Social links grid */}
        <div className="space-y-3 mb-8" data-testid="follow-links">
          {entries.map(([k, meta]) => (
            <a key={k} href={links[k]} target="_blank" rel="noreferrer"
              onClick={() => api.post("/events", { event: "follow_outbound", props: { network: k } }).catch(() => {})}
              className="card-noir p-4 flex items-center justify-between hover:border-[#8b0000] transition-colors"
              data-testid={`follow-${k}`}>
              <div className="flex items-center gap-3">
                <div className="text-[#f4f0ec]">{customIcon(k)}</div>
                <div className="font-display text-lg text-left">{meta.label}</div>
              </div>
              <div className="text-xs tracking-widest text-[#a39797]">OPEN →</div>
            </a>
          ))}
        </div>

        {/* Email signup */}
        <div className="card-noir p-5 mb-6" data-testid="follow-email-form">
          <div className="text-xs tracking-widest text-[#a39797] mb-3">
            <Mail size={12} className="inline mr-2" /> OR GET IT IN YOUR INBOX
          </div>
          <form onSubmit={subscribe} className="flex items-center gap-2 flex-wrap">
            <input type="email" required className="input-noir flex-1 min-w-[220px]"
              placeholder="your email"
              value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="follow-email" />
            <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-50"
              data-testid="follow-subscribe">
              {submitting ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />}
              {submitting ? "joining…" : "Join"}
            </button>
          </form>
          <div className="text-xs text-[#a39797] mt-3">
            Low volume. Unsubscribe one-click. No spoilers.
          </div>
        </div>

        <button onClick={sharePage} className="btn-ghost text-sm" data-testid="follow-share">
          <Share2 size={14} /> Share this page
        </button>
      </div>
    </div>
  );
};

export default Follow;
